import { loginUrl, memberUrl } from "../../core/constants/endpoints";
import { UserModel } from "../model/UserModel";
import HttpClient from "../../core/helpers/HttpClient";

/**
 * A member plus the token the auth service actually answered with — which is
 * not always the token the call was made with. See `getUserByToken`.
 */
export interface UserSession {
  readonly user: UserModel;
  readonly token: string;
}

export abstract class UserRemoteDataSource {
  abstract login(email: string, password: string): Promise<string>;

  abstract logout(): void;

  abstract getUserByToken(token: string): Promise<UserSession>;
}

export class UserRemoteDataSourceImpl extends UserRemoteDataSource {
  httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    super();
    this.httpClient = httpClient;
  }

  login = (email: string, password: string) =>
    this.httpClient.request<string>(loginUrl, {
      data: { email, password },
      factory: (_, headers) => {
        this.httpClient.authenticateRequests(headers?.authorization);
        return headers?.authorization;
      },
    });

  logout = () => this.httpClient.unauthenticateRequests();

  /**
   * PPI-1848: adopts the rotated token when the auth service sends one.
   *
   * `pivot-health-authentication` mints a fresh token and returns it on the
   * `Authorization` response header whenever the incoming token has under
   * ten minutes left (JwtRequestFilter.kt), and exposes that header to the
   * browser via CORS (CorsClass.kt). Until now only `login` read it, so a
   * member who never revisited the login page had their rotation silently
   * discarded and eventually got signed out mid-session. That was the bug.
   *
   * Falling back to the request token when the header is absent is what
   * keeps ordinary calls correct: the server deliberately sends nothing back
   * when the token is nowhere near expiry, and that is not an error.
   */
  getUserByToken = (token: string) =>
    this.httpClient.request<UserSession>(memberUrl, {
      // Explicit header, even though the armed axios interceptor overwrites
      // it on every renewal-driven call. It is load-bearing only on the
      // magic-link path, where no interceptor exists yet; the two values are
      // identical whenever both apply. Removing it would silently break
      // ?token= sign-in.
      headers: { Authorization: `Bearer ${token}` },
      factory: (data, headers) => {
        const rotatedToken: string = headers?.authorization || token;
        this.httpClient.authenticateRequests(rotatedToken);
        return { user: UserModel.fromJson(data), token: rotatedToken };
      },
    });
}
